/**
 * Report Registry
 *
 * Central registry of available reports/data views.
 * Used by ReportsHub to generate the report list and handle navigation/rendering.
 *
 * Optional visibility (per entry):
 * - feature: string – key in whoami.features (e.g. 'training'). Report shown only when that feature is true for the tenant.
 * - tenantCodes: string[] – report shown only when current tenant code is in this list. Omit for all tenants.
 * - onHold: true – report is hidden from the UI (code remains). Used when a report is paused until a decision (e.g. data model). See docs/EXTENDED_SERVICES_REPORTS_ON_HOLD.md.
 * Entries without feature/tenantCodes are shared (visible to all tenants).
 */

export const reportRegistry = [
  // Export section: tier-one data exports (flat CSV). Customers own their data.
  {
    id: 'export-clients',
    name: 'Clients',
    description: 'Download all client records with lookup values (race, gender, county, etc.)',
    category: 'Export',
    kind: 'export',
    endpoints: { exportCsvUrl: 'clients.csv' },
    exportFilename: 'clients_export.csv',
    permission: 'reports.view_export',
  },
  {
    id: 'export-contacts',
    name: 'Contacts',
    description: 'Download all contact records',
    category: 'Export',
    kind: 'export',
    endpoints: { exportCsvUrl: 'contacts.csv' },
    exportFilename: 'contacts_export.csv',
    permission: 'reports.view_export',
  },
  {
    id: 'export-payees',
    name: 'Payees',
    description: 'Download all payee records (first address per payee; multiple addresses summarized)',
    category: 'Export',
    kind: 'export',
    endpoints: { exportCsvUrl: 'payees.csv' },
    exportFilename: 'payees_export.csv',
    permission: 'reports.view_export',
  },
  {
    id: 'export-providers',
    name: 'Providers',
    description: 'Download all provider records (first site per provider; additional sites summarized)',
    category: 'Export',
    kind: 'export',
    endpoints: { exportCsvUrl: 'providers.csv' },
    exportFilename: 'providers_export.csv',
    permission: 'reports.view_export',
  },
  {
    id: 'srfacs-contacts',
    name: 'SRfax Contacts',
    description: 'Provider fax contact list for SRFax: Name and fax number, one row per provider with a fax number. No header row. Matches legacy ProviderSRfaxContacts export.',
    category: 'Export',
    kind: 'export',
    endpoints: { exportCsvUrl: 'srfacs-contacts.csv' },
    exportFilename: 'SRfaxContacts.csv',
    permission: 'reports.view_export',
  },
  {
    id: 'export-school-districts',
    name: 'School Districts',
    description: 'Download all school district records (contacts summarized in one column)',
    category: 'Export',
    kind: 'export',
    endpoints: { exportCsvUrl: 'school-districts.csv' },
    exportFilename: 'school_districts_export.csv',
    permission: 'reports.view_export',
  },
  // Executive (legacy report #33: Caseload Tools Intake, Active IFSP)
  {
    id: 'caseload-tools',
    name: 'Caseload Tools (Intake, Active IFSP)',
    description: 'Open caseload by phase: Intake (pre-IFSP) and Active IFSP. Paper-ready; use Print to print.',
    category: 'Executive',
    kind: 'report',
    runMode: 'inline',
    component: 'CaseloadToolsReport',
    endpoints: { runUrl: 'caseload-tools' },
    permission: 'reports.view_executive',
  },
  // Clients: Termination split into two canonical reports (moved from Tracking nav group).
  {
    id: 'termination-detailed',
    name: 'Termination - Detailed',
    description: 'Closed referrals with row-level detail and interactive filtering.',
    category: 'Clients',
    kind: 'report',
    runMode: 'inline',
    component: 'TerminationCanonicalReport',
    endpoints: { runUrl: 'termination-detailed' },
    permissionAny: ['reports.view_clients', 'reports.view_tracking'],
  },
  {
    id: 'termination-summary',
    name: 'Termination - Summary',
    description: 'Termination counts by reason, including intake/IFSP breakdown.',
    category: 'Clients',
    kind: 'report',
    runMode: 'inline',
    component: 'TerminationCanonicalReport',
    endpoints: { runUrl: 'termination-summary' },
    permissionAny: ['reports.view_clients', 'reports.view_tracking'],
  },
  // Reconciliation: TPC (Access vs Cornerstone) (legacy #34). Upload ClientEIT file from Foxfire, then view comparison.
  {
    id: 'tpc-access-vs-cornerstone',
    name: 'TPC Reconciliation (Access vs Cornerstone)',
    description: 'Compare TPC data in this system with Cornerstone (ClientEIT file). Run Foxfire ClientEIT first, then upload the .csv file.',
    category: 'Reconciliation',
    kind: 'report',
    runMode: 'inline',
    component: 'TpcAccessVsCornerstoneReport',
    endpoints: { runUrl: 'tpc-access-vs-cornerstone' },
    permission: 'reports.view_reconciliation',
  },
  // Reconciliation: Exit (Access vs Cornerstone) (legacy #50). Upload chldeild file from Foxfire Exitrpt, then view mismatches.
  {
    id: 'exit-access-vs-cornerstone',
    name: 'Exit Reconciliation (Access vs Cornerstone)',
    description: 'Compare Access Exit date with Cornerstone latest visit date (chldeild import). Run Foxfire Exitrpt first, then upload the .csv/.txt file.',
    category: 'Reconciliation',
    kind: 'report',
    runMode: 'inline',
    component: 'ExitAccessVsCornerstoneReport',
    endpoints: { runUrl: 'exit-access-vs-cornerstone' },
    permission: 'reports.view_reconciliation',
  },
  // Tracking (manager-only): requires reports.view_tracking — not reports.view_tracking_staff (Service Coordinators).
  {
    id: 'service-delay-workflow',
    name: 'Service Delay Workflow',
    description: 'Delay operations workflow with four sections: new, resolved, DHS reporting edit queue, and closed-not-resolved exceptions. Changes (where the child already has that service) are excluded — a change is not a delay and is never reported to the state.',
    category: 'Tracking',
    kind: 'report',
    runMode: 'inline',
    component: 'ServiceDelayWorkflowReport',
    endpoints: { runUrl: 'service-delay-workflow' },
    permission: 'reports.view_tracking',
  },
  // Tracking (manager-only): same as service-delay-workflow; SCs use view_tracking_staff for a smaller set of reports.
  {
    id: 'script-tasks-pending',
    name: 'Scripts - Pending Receipt',
    description:
      'Script task, client (last, first), DOB, Service Coordinator, request date, and pediatrician name/phone from the client record for follow-up.',
    category: 'Tracking',
    kind: 'report',
    runMode: 'inline',
    component: 'TerminationCanonicalReport',
    endpoints: { runUrl: 'script-tasks-pending' },
    permission: 'reports.view_tracking',
  },
  // Tracking: Client Delay List (legacy Team Tracking Filter option 5). Open referrals with active provider-team delays. Runnable by SC (view_tracking_staff) and managers (view_tracking).
  {
    id: 'client-delay-list',
    name: 'Client Delay List',
    description: 'Open referrals with an active delay or "Waiting" provider slot (e.g. Waiting DT/ST). Delays and Changes are shown in separate sections — a "Change" (the child already has that service) is tracked but not reported to the state. Archived provider-team lines are excluded. Sort and filter by Service Coordinator.',
    category: 'Tracking',
    kind: 'report',
    runMode: 'inline',
    component: 'ClientDelayListReport',
    endpoints: { runUrl: 'client-delay-list' },
    permissionAny: ['reports.view_tracking', 'reports.view_tracking_staff'],
  },
  // Clients: Age Out Next Month by SC (legacy #51). Open clients whose 3rd birthday is in the next calendar month, by Service Coordinator.
  {
    id: 'age-out-next-month-by-sc',
    name: 'Age Out Next Month by SC',
    description: 'Open clients whose 3rd birthday falls in the next calendar month, grouped by Service Coordinator. No date parameters; uses current month + 1.',
    category: 'Clients',
    kind: 'report',
    runMode: 'inline',
    component: 'AgeOutNextMonthBySCReport',
    endpoints: { runUrl: 'age-out-next-month-by-sc' },
    permission: 'reports.view_clients',
  },
  // Clients: Age Out by District (legacy #309). Pick district and start/stop date; open clients whose 3rd birthday is in range, in that district.
  {
    id: 'age-out-by-district',
    name: 'Age Out by District',
    description: 'Open clients whose 3rd birthday falls within a date range, filtered by school district. Select a district and start/stop dates, then run.',
    category: 'Clients',
    kind: 'report',
    runMode: 'inline',
    component: 'AgeOutByDistrictReport',
    endpoints: { runUrl: 'age-out-by-district' },
    permission: 'reports.view_clients',
  },
  // Tracking (manager-only): team-wide review; SCs do not get this (view_tracking only).
  {
    id: 'team-review',
    name: 'Team Review Report',
    description: 'Open clients with tracking dates (Open, Intake, IFSP, 6-month and annual reviews), grouped by staff. Filter by date range and team.',
    category: 'Tracking',
    kind: 'report',
    runMode: 'inline',
    component: 'TeamReviewReport',
    endpoints: { runUrl: 'team-review' },
    permission: 'reports.view_tracking',
  },
  // Clients: Referral Source Report (legacy admin report 41). Referrals by date range with Level 1/Level 2 source; filter by column headers.
  {
    id: 'referral-source',
    name: 'Referral Source Report',
    description: 'Referrals by date range with Level 1 and Level 2 source. See where referrals are coming from; filter by column headers.',
    category: 'Clients',
    kind: 'report',
    runMode: 'inline',
    component: 'TerminationCanonicalReport',
    endpoints: { runUrl: 'referral-source' },
    permission: 'reports.view_clients',
  },
  // Tracking (manager-only): incentive / transition metrics; SCs do not get this (view_tracking only).
  {
    id: 'transition-completed-on-time',
    name: 'Transition Completed on Time',
    description: 'Clients whose transition due date (Age 3 − 91 days) falls in the selected date range, with TPM actual date and on-time status. Incentive Payment #2 (Line 31 on EI Statistical Report).',
    category: 'Tracking',
    kind: 'report',
    runMode: 'inline',
    component: 'TerminationCanonicalReport',
    endpoints: { runUrl: 'transition-completed-on-time' },
    permission: 'reports.view_tracking',
  },
  // Clients: Referral Level 2 Outcome (legacy admin option 48). Outcomes by Level 2 source; open date range + Level 2 filter.
  {
    id: 'referral-level-2-outcome',
    name: 'Referral Level 2 Outcome',
    description: 'For a selected Referral Level 2 source (or sources), referrals whose effective open date falls in the date range, with outcome status (Active/Closed), termination reason, and IFSP date.',
    category: 'Clients',
    kind: 'report',
    runMode: 'inline',
    component: 'TerminationCanonicalReport',
    endpoints: { runUrl: 'referral-level-2-outcome' },
    permission: 'reports.view_clients',
  },
  // Tracking: Client Tracking Due (legacy admin option 8). Visible to SCs (own caseload only) or managers (staff picker).
  {
    id: 'ct-due',
    name: 'Client Tracking Due',
    description: 'Open referrals with tracking due dates that fall on or before a chosen "due by" date. Shows 6‑month and annual IFSP due dates, School, Transition (TPC), Transition Plan (TPM), and Exit due. Due cells stay blank when a milestone uses a tracking special case (e.g. 12‑month IFSP Extended to age three), consistent with the dashboard.',
    category: 'Tracking',
    kind: 'report',
    runMode: 'inline',
    component: 'TerminationCanonicalReport',
    endpoints: { runUrl: 'ct-due' },
    permissionAny: ['reports.view_tracking', 'reports.view_tracking_staff'],
  },
  // Clients: Client Ineligible Report (legacy admin option 22). Closed referrals in date range with ineligible termination reason (33* or 34).
  {
    id: 'client-ineligible',
    name: 'Client Ineligible Report',
    description: 'Referrals closed in the selected date range whose termination reason is ineligible (code 33* or 34). Client name in Last, First format.',
    category: 'Clients',
    kind: 'report',
    runMode: 'inline',
    component: 'TerminationCanonicalReport',
    endpoints: { runUrl: 'client-ineligible' },
    permission: 'reports.view_clients',
  },
  // Clients: Client Open Referrals (legacy admin option 30). Referrals with open date in range; optional Open only filter.
  {
    id: 'client-open-referrals',
    name: 'Client Open Referrals',
    description: 'Referrals whose open date falls in the selected range. One row per referral; client name in Last, First format.',
    category: 'Clients',
    kind: 'report',
    runMode: 'inline',
    component: 'TerminationCanonicalReport',
    endpoints: { runUrl: 'client-open-referrals' },
    permission: 'reports.view_clients',
  },
  // Clients: IFSP Plan Due (legacy admin option 31). Open referrals with no Initial IFSP 46+ days past open; as-of date filter.
  {
    id: 'ifsp-plan-due',
    name: 'IFSP Plan Due',
    description: 'Open referrals with no completed Initial IFSP 46 or more days after open date. Choose an "as of" date; sorted by Open Date.',
    category: 'Clients',
    kind: 'report',
    runMode: 'inline',
    component: 'TerminationCanonicalReport',
    endpoints: { runUrl: 'ifsp-plan-due' },
    permission: 'reports.view_clients',
  },
  // Clients: Client Under Age 1 Report (legacy admin option 27). Clients under 1 year old; sorted by Days To 1.
  {
    id: 'under-1',
    name: 'Client Under Age 1 Report',
    description: 'Clients under age 1 as of today. One row per client; open date is open or reopen (whichever is latest). Sorted by Days To 1.',
    category: 'Clients',
    kind: 'report',
    runMode: 'inline',
    component: 'TerminationCanonicalReport',
    endpoints: { runUrl: 'under-1' },
    permission: 'reports.view_clients',
  },
  {
    id: 'open-client-needs',
    name: 'Open Client Needs',
    description: 'Open client needs report with canonical filtering (includes DGN filter scenario).',
    category: 'Provider / Needs',
    kind: 'report',
    runMode: 'inline',
    component: 'TerminationCanonicalReport',
    endpoints: { runUrl: 'open-client-needs' },
    permission: 'reports.view_client_needs',
  },
  {
    id: 'providers-by-type-canonical',
    name: 'Providers by Type',
    description: 'Provider listing; filter by one or more provider types and other columns.',
    category: 'Provider / Needs',
    kind: 'report',
    runMode: 'inline',
    component: 'TerminationCanonicalReport',
    endpoints: { runUrl: 'providers-by-type-canonical' },
    permissionAny: ['reports.view_client_needs', 'reports.view_tracking_staff'],
  },
  // Hidden from Reports hub for now (functionality lives under Client Services → Search Providers).
  // Remove `onHold` to show again; backend canonical routes unchanged.
  {
    id: 'search-providers',
    name: 'Search Providers',
    description: 'Search providers by type, credentials, service area, language; export or use for email. Same as legacy Option 1.',
    category: 'Provider / Needs',
    kind: 'report',
    runMode: 'inline',
    component: 'TerminationCanonicalReport',
    endpoints: { runUrl: 'search-providers' },
    permissionAny: ['reports.view_client_needs', 'reports.view_tracking_staff'],
    onHold: true,
  },
  // Extended Services (legacy options 72, 73, 74, 104). Data: referral_eies_tracking + referral_milestones.
  // Option 104: Extended Services Report — canonical table, viewable by managers and SCs; SC sees own caseload.
  {
    id: 'extended-services-report',
    name: 'Extended Services Report',
    description: 'Open clients in the Extended Services window (3rd birthday May–Aug). Sort and filter by SC.',
    category: 'School / ES',
    kind: 'report',
    runMode: 'inline',
    component: 'ExtendedServicesReportPage',
    endpoints: { runUrl: 'extended-services-report' },
    permissionAny: ['reports.view_extended_services', 'reports.view_tracking_staff'],
  },
  {
    id: 'extended-services-admin',
    name: 'Extended Services Admin Report',
    description: 'Open clients in the Extended Services window (3rd birthday May–Aug), by school then birthdate',
    category: 'School / ES',
    kind: 'grid',
    runMode: 'inline',
    component: 'ExtendedServicesReport',
    endpoints: { runUrl: 'extended-services-admin', exportCsvUrl: 'extended-services-admin.csv' },
    exportFilename: 'extended_services_admin.csv',
    permission: 'reports.view_extended_services',
  },
  {
    id: 'extended-services-lea',
    name: 'Potential Extended Services for LEA',
    description: 'Open clients in ES window with IFSP and school referral dates (no 1950 decline), for LEA planning',
    category: 'School / ES',
    kind: 'grid',
    runMode: 'inline',
    component: 'ExtendedServicesReport',
    endpoints: { runUrl: 'extended-services-lea', exportCsvUrl: 'extended-services-lea.csv' },
    exportFilename: 'extended_services_potential_lea.csv',
    permission: 'reports.view_extended_services',
  },
  {
    id: 'extended-services-receiving',
    name: 'Extended Services Receiving Report',
    description: 'Open clients with IEP eligible date and ES accepted = Yes, by school then school start',
    category: 'School / ES',
    kind: 'grid',
    runMode: 'inline',
    component: 'ExtendedServicesReport',
    endpoints: { runUrl: 'extended-services-receiving', exportCsvUrl: 'extended-services-receiving.csv' },
    exportFilename: 'extended_services_receiving.csv',
    permission: 'reports.view_extended_services',
  },
  // Tools: Travel Voucher Helper (feature-gated). Export completed tracking items for travel voucher; starting address from tenant, optional staff filter.
  {
    id: 'travel-voucher-helper',
    name: 'Travel Voucher Helper',
    description: 'Export travel voucher data for a date range: one row per completed tracking item (Initial IFSP, 6‑month/annual IFSP, TPC, Exit, etc.) with starting/ending addresses. Enter one-way miles in the tool; CSV includes Round trip (Yes/No) and total mileage for paste into finance spreadsheets.',
    category: 'Tools',
    kind: 'report',
    runMode: 'inline',
    component: 'TravelVoucherHelperReport',
    endpoints: { runUrl: 'travel-voucher-export', exportCsvUrl: 'travel-voucher-export.csv' },
  },
  // Contacts: Caseload Contacts (legacy SC Report option 6 – Export Caseload Contacts). Primary contacts for open clients by SC; view and export CSV.
  {
    id: 'caseload-contacts',
    name: 'Caseload Contacts',
    description: 'Primary contacts for open clients assigned to the selected Service Coordinator. View in table and export to CSV.',
    category: 'Contacts',
    kind: 'report',
    runMode: 'inline',
    component: 'CaseloadContactsReport',
    endpoints: { runUrl: 'caseload-contacts' },
  },
  // Schools: School Referral Needed (legacy SC Report option 33). Open clients 27–31 months with no school referral date.
  {
    id: 'school-referral-needed',
    name: 'School Referral Needed',
    description: 'Open clients aged 27–31 months with no school referral date yet. Optional staff filter; sort by birthdate or staff. Export or print.',
    category: 'School / ES',
    kind: 'report',
    runMode: 'inline',
    component: 'SchoolReferralNeededReport',
    endpoints: { runUrl: 'school-referral-needed' },
    permissionAny: ['reports.view_tracking', 'reports.view_clients'],
  },
  {
    id: 'school-low-incident-disability',
    name: 'School Low Incident Disability',
    description:
      'Open referrals whose client has at least one Diagnosis Dx1–Dx3 marked Low incident disability. Columns: client, Age 3 (third birthday) with date-range filter, qualifying diagnosis text, Staff, district code. Sort from column headers; use filters (Age 3 uses Start/Stop dates). Client names on screen only; Export CSV and Print omit names for school-facing privacy.',
    category: 'School / ES',
    kind: 'report',
    runMode: 'inline',
    component: 'SchoolLowIncidentDisabilityReport',
    endpoints: { runUrl: 'school-low-incident-disability' },
    permissionAny: ['reports.view_tracking', 'reports.view_clients'],
  },
  {
    id: 'school-referral-completed',
    name: 'School Referral Completed',
    description:
      'Open referrals where the School milestone is completed: client (last, first), date of birth, school completed date, Staff, and district code (school district number). Sort and filter from column headers; Export CSV and Print.',
    category: 'School / ES',
    kind: 'report',
    runMode: 'inline',
    component: 'TerminationCanonicalReport',
    endpoints: { runUrl: 'school-referral-completed' },
    permissionAny: ['reports.view_tracking', 'reports.view_clients'],
  },
  // Training (feature-gated). Activity: SC and managers with training.read; Monitor: training.monitor only.
  {
    id: 'training-activity',
    name: 'Training Activity Report',
    description: 'Training activities in the current window. Filter by staff (managers can pick staff).',
    category: 'Training',
    kind: 'report',
    runMode: 'inline',
    component: 'TrainingActivityReport',
    feature: 'training',
    permission: 'training.read',
    endpoints: { runUrl: 'training/reports/activity' },
  },
  {
    id: 'training-monitor',
    name: 'Training Monitor Report',
    description: 'Staff whose total training hours in the current window are at or below a threshold (who is short).',
    category: 'Training',
    kind: 'report',
    runMode: 'inline',
    component: 'TrainingMonitorReport',
    feature: 'training',
    permission: 'training.monitor',
    endpoints: { runUrl: 'training/reports/monitor' },
  },
];

/**
 * Get reports visible for the given tenant, features, and permissions.
 * If tenantCode/features omitted, returns full registry (caller not yet context-aware).
 * When permission is set on a report, user must have that permission (permissions array) to see it.
 * When permissionAny is set (array), user must have at least one of those permissions to see the report.
 */
export function getVisibleReports(tenantCode, features = {}, permissions = []) {
  if (tenantCode == null && (features == null || Object.keys(features).length === 0)) {
    return [...reportRegistry];
  }
  const permSet = Array.isArray(permissions) ? new Set(permissions) : new Set();
  return reportRegistry.filter((r) => {
    if (r.onHold === true) return false;
    if (r.feature != null && r.feature !== '') {
      if (!features || features[r.feature] !== true) return false;
    }
    if (r.permissionAny != null && Array.isArray(r.permissionAny) && r.permissionAny.length > 0) {
      if (!r.permissionAny.some((p) => permSet.has(p))) return false;
    } else if (r.permission != null && r.permission !== '' && !permSet.has(r.permission)) return false;
    if (r.tenantCodes != null && Array.isArray(r.tenantCodes) && r.tenantCodes.length > 0) {
      const code = (tenantCode || '').toString().toUpperCase();
      if (!r.tenantCodes.some((c) => (c || '').toString().toUpperCase() === code)) return false;
    }
    return true;
  });
}

/**
 * Ordered list of high-level report categories for the left nav.
 * Reports Hub only shows groups that have ≥1 visible report for the user (see getReportNavCategoriesVisible).
 */
/** Left nav: report sections in display order. */
export const REPORT_NAV_CATEGORIES = [
  'Provider / Needs',
  'Clients',
  'Contacts',
  'Executive',
  'Export',
  'Reconciliation',
  'School / ES',
  'Tracking',
  'Tools',
  'Training',
];

/**
 * Categories to show in the Reports Hub left rail: same order as REPORT_NAV_CATEGORIES,
 * omitting groups where the current user has no visible reports (permissions + features).
 */
export function getReportNavCategoriesVisible(tenantCode, features, permissions) {
  const byCategory = getReportsByCategory(tenantCode, features, permissions);
  return REPORT_NAV_CATEGORIES.filter((cat) => (byCategory.get(cat) || []).length > 0);
}

/**
 * Get all unique categories from the registry (optionally from visible reports only).
 */
export const getCategories = (tenantCode, features, permissions) => {
  const reports = tenantCode != null || features != null ? getVisibleReports(tenantCode, features, permissions) : reportRegistry;
  const categories = new Set(reports.map((r) => r.category));
  return ['All', ...Array.from(categories).sort()];
};

/**
 * Get visible reports grouped by category. Returns a Map: category -> report[].
 * Reports within each category are sorted alphabetically by name (A–Z, case-insensitive)
 * for left-to-right, top-to-bottom card layout.
 * For nav, use getReportNavCategoriesVisible() for the left rail; for each category
 * get reports from this map (empty array if none).
 */
export function getReportsByCategory(tenantCode, features, permissions) {
  const reports = getVisibleReports(tenantCode, features, permissions);
  const byCategory = new Map();
  for (const r of reports) {
    const cat = r.category || 'Other';
    if (!byCategory.has(cat)) byCategory.set(cat, []);
    byCategory.get(cat).push(r);
  }
  for (const [cat, list] of byCategory) {
    byCategory.set(cat, [...list].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })));
  }
  return byCategory;
}

/**
 * Get report by ID (optionally only if visible for tenant/features).
 */
export const getReportById = (id, tenantCode, features, permissions) => {
  const report = reportRegistry.find((r) => r.id === id);
  if (!report) return undefined;
  if (tenantCode != null || (features != null && Object.keys(features || {}).length > 0)) {
    const visible = getVisibleReports(tenantCode, features || {}, permissions);
    if (!visible.some((r) => r.id === id)) return undefined;
  }
  return report;
};

/**
 * Filter reports by category and search term. Optionally restrict to visible reports first.
 */
export const filterReports = (category, searchTerm, tenantCode, features, permissions) => {
  let filtered =
    tenantCode != null || (features != null && Object.keys(features || {}).length > 0)
      ? getVisibleReports(tenantCode, features || {}, permissions)
      : [...reportRegistry];

  if (category && category !== 'All') {
    filtered = filtered.filter((r) => r.category === category);
  }

  if (searchTerm && searchTerm.trim()) {
    const term = searchTerm.toLowerCase();
    filtered = filtered.filter(
      (r) =>
        r.name.toLowerCase().includes(term) ||
        r.description.toLowerCase().includes(term) ||
        r.category.toLowerCase().includes(term)
    );
  }

  return filtered;
};
